// Runtime card-metadata lookup + filtering helpers for the deck builder.
//
// cardData.json is generated by src/scripts/buildcarddata.cjs (merged from the
// scraper output) and is keyed by card NAME -> details. See that script.

import cardData from "./cardData.json";
import cardPrintings from "./cardPrintings.json";
import { doubleEvoDetails } from "./doubleEvo";

// Dual-sided evolve cards aren't in the scraped data under their in-game names
// (only as "<A-side> Evolved") — doubleEvoDetails covers both of their faces.
const lookup = (name) => cardData[name] || doubleEvoDetails(name);

export const getDetails = (name) => lookup(name) || null;

// Some cards exist under more than one name but are the very same card — alt-art
// collab cards, e.g. "Anastasia" and "Anastasia [All-Out Vacation]". They share
// a single deckbuilding copy limit: 3 of one + 3 of the other (6 total) is
// illegal; the cap of 3 is across the group. Two names are the same card when
// they share a base name (the part before any "[...]" suffix) AND have identical
// class/type/cost/stats/effect. The signature guards against same-character but
// different cards (e.g. "Anastasia" vs the cheaper "Anastasia [Seize the
// Light]"). Built once from cardData; auto-detects future alt-art cards.
const aliasBaseName = (n) =>
  n.replace(/\s*\[[^\]]*\]/g, "").replace(/\s+Evolved$/, "").trim();
const cardSignature = (c) =>
  [c.class, c.cardType, c.cost, c.attack, c.defense, c.effect].join("|||");

const SAME_CARD_NAMES = (() => {
  const groups = new Map();
  for (const name in cardData) {
    if (name.endsWith(" TOKEN")) continue;
    const key = aliasBaseName(name) + "###" + cardSignature(cardData[name]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(name);
  }
  const byName = {};
  for (const names of groups.values()) {
    if (names.length < 2) continue;
    for (const n of names) byName[n] = names;
  }
  return byName;
})();

// All names that share a copy limit with `name` — the card itself plus any
// same-card aliases. Returns just [name] for a card with no alias. The returned
// array is shared by every member of a group, so it doubles as a stable group
// identity (e.g. via .join()).
export const sameNameCards = (name) => SAME_CARD_NAMES[name] || [name];

// Collab "classes" aren't stored in the `class` field — those cards carry a base
// craft (often neutral) — but they're identifiable by trait. Detect them so a
// collab deck is labelled by its collab rather than its base craft.
const collabClass = (name) => {
  const d = lookup(name);
  const t = (d && d.trait) || "";
  if (/Vanguard/i.test(t)) return "vanguard";
  if (/iM@S/i.test(t)) return "idolmaster";
  if (/Umamusume|Tracen Academy/i.test(t)) return "umamusume";
  return null;
};

// A card's class for deck-identity purposes: its collab class if it is a collab
// card, otherwise its base craft (forest/sword/.../neutral).
export const cardClass = (name) =>
  collabClass(name) || (lookup(name) && lookup(name).class) || "";

// A deck's overall class = its most common non-neutral card class. Most decks
// are mono-class; for a rare multi-class deck the majority wins (e.g. 21 dragon
// + 19 sword -> dragon). Neutral cards don't vote. Returns "" for an empty or
// all-neutral deck. Used as a fallback when a deck has no explicit class set.
export const computeDeckClass = (cards) => {
  const tally = {};
  for (const name of cards || []) {
    const c = cardClass(name);
    if (!c || c === "neutral") continue;
    tally[c] = (tally[c] || 0) + 1;
  }
  let best = "";
  let bestN = 0;
  for (const c in tally) {
    if (tally[c] > bestN) {
      best = c;
      bestN = tally[c];
    }
  }
  return best;
};

// Numeric cost, or null for cards that show "-"/"X"/no cost (leaders, most
// evolved followers, tokens). Used for cost bucketing + the mana curve.
export const getCost = (name) => {
  const d = lookup(name);
  if (!d) return null;
  const n = parseInt(d.cost, 10);
  return Number.isNaN(n) ? null : n;
};

// The site lists composite card types ("Follower / Evolved", "Spell / Token").
// We filter on the primary (first) segment.
export const primaryType = (name) => {
  const d = lookup(name);
  if (!d || !d.cardType) return "";
  return d.cardType.split("/")[0].trim();
};

// Traits are "/"-separated composites ("Arcana / Mage / Cutthroat"). Atomic
// tokens are far more useful for filtering than the 400+ raw combinations.
export const traitTokens = (name) => {
  const d = lookup(name);
  if (!d || !d.trait || d.trait === "-") return [];
  return d.trait.split("/").map((t) => t.trim()).filter(Boolean);
};

// ---- Distinct filter option lists (computed once) ----

export const CARD_TYPES = ["Follower", "Spell", "Amulet"];

// Fixed display order; only those actually present are kept.
const RARITY_ORDER = [
  "Bronze", "Silver", "Gold", "Legendary", "Super Legendary",
  "Ultimate", "Special", "Super Special", "Premium", "Promo",
];

export const RARITIES = (() => {
  const present = new Set();
  // Options come from both the name-keyed data (dedup view) and the per-printing
  // data (the "show all printings" view exposes alt-art rarities like Ultimate).
  for (const name in cardData) {
    const r = cardData[name].rarity;
    if (r && r !== "-") present.add(r);
  }
  for (const p of cardPrintings) {
    if (p.rarity && p.rarity !== "-") present.add(p.rarity);
  }
  // Keep the fixed order; append any present rarity not in the list at the end.
  const ordered = RARITY_ORDER.filter((r) => present.has(r));
  for (const r of present) if (!RARITY_ORDER.includes(r)) ordered.push(r);
  return ordered;
})();

export const TRAITS = (() => {
  const set = new Set();
  for (const name in cardData) {
    for (const t of (cardData[name].trait || "").split("/")) {
      const v = t.trim();
      if (v && v !== "-") set.add(v);
    }
  }
  return [...set].sort();
})();

export const COST_BUCKETS = ["0", "1", "2", "3", "4", "5", "6", "7", "8+"];
export const STAT_BUCKETS = ["0", "1", "2", "3", "4", "5", "6", "7", "8+"];

const numOrNull = (s) => {
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? null : n;
};
const bucket = (n) => (n == null ? null : n >= 8 ? "8+" : String(n));

// filters: { types, traits, rarities, costs, attacks, defenses } — each an array;
// empty array = no constraint for that category. Categories combine with AND.
export const matchesFilters = (name, filters) => {
  if (!filters) return true;
  const { types = [], traits = [], rarities = [], costs = [], attacks = [], defenses = [] } = filters;
  const d = lookup(name);
  if (types.length) {
    if (!d || !types.includes(primaryType(name))) return false;
  }
  if (rarities.length) {
    if (!d || !rarities.includes(d.rarity)) return false;
  }
  if (costs.length) {
    const b = bucket(getCost(name));
    if (b == null || !costs.includes(b)) return false;
  }
  if (attacks.length) {
    const b = bucket(d ? numOrNull(d.attack) : null);
    if (b == null || !attacks.includes(b)) return false;
  }
  if (defenses.length) {
    const b = bucket(d ? numOrNull(d.defense) : null);
    if (b == null || !defenses.includes(b)) return false;
  }
  if (traits.length) {
    const tokens = traitTokens(name);
    if (!traits.every((t) => tokens.includes(t))) return false;
  }
  return true;
};

export const hasActiveFilters = (filters) =>
  !!filters &&
  ["types", "traits", "rarities", "costs", "attacks", "defenses"].some(
    (k) => filters[k] && filters[k].length
  );
